import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { routes } from '../routes'
import Follow from '../components/Follow'
import { DownloadGraphics } from '../components/DownloadGraphics'
import { Donate, type DonateOrg } from '../components/Donate'
import { khersonSources } from '../data/follow'
import { fellas4europe } from '../data/donateOrgs'
import { humanSafariNews } from '../data/humanSafariNews'
import { ResourceGroups } from '../components/ResourceGroups'
import MediaCarousel from '../components/MediaCarousel/MediaCarousel'
import { khersonMedia } from '../assets/media'

import ahsImg from 'url:../assets/anti-human-safari.jpg'
import khsImg from 'url:../assets/KhersonHumanSafari.jpg'
import stopHumanSafariPoster from 'url:../assets/posters/dec-2025/StopHumanSafari.png'
import stopHumanSafariEye from 'url:../assets/human-safari-kherson.jpg'

import webIcon from 'url:../assets/web.png'
import facebookIcon from 'url:../assets/facebook.png'
import instagramIcon from 'url:../assets/instagram.png'
import blueskyIcon from 'url:../assets/bluesky.png'
import xIcon from 'url:../assets/x.png'

// Download graphics section
const graphics = [
  { src: stopHumanSafariPoster, city: 'none' },
  { src: stopHumanSafariEye, city: 'none' },
]

const shareLinks = {
  facebook: 'https://www.facebook.com/sharer/sharer.php?u=https://humansafari.org',
  x: 'https://twitter.com/intent/tweet?text=%23StopHumanSafari%20Russian%20drones%20hunt%20civilians%20in%20Kherson.%20This%20war%20crime%20must%20stop%3A%20https%3A%2F%2Fhumansafari.org%20%23HumansAreNotTargets%20%23SaveKherson',
  bluesky:
    'https://bsky.app/intent/compose?text=%23StopHumanSafari%20Russian%20drones%20hunt%20civilians%20in%20Kherson.%20This%20war%20crime%20must%20stop%3A%20https%3A%2F%2Fhumansafari.org%20%23HumansAreNotTargets%20%23SaveKherson',
}

const orgs: DonateOrg[] = [
  fellas4europe,
  {
    key: 'ahs',
    img: ahsImg,
    donateUrl: 'https://www.gofundme.com/f/save-kherson-civilians-from-human-safari',
    socialLinks: [
      {
        asset: webIcon,
        labelKey: 'help.website',
        href: 'https://stophumansafari.com/?utm_source=humansafari',
      },
      {
        asset: instagramIcon,
        labelKey: 'help.instagram',
        href: 'https://www.instagram.com/anti_human_safari/',
      },
      {
        asset: blueskyIcon,
        labelKey: 'help.bluesky',
        href: 'https://bsky.app/profile/did:plc:6npazd7klynj55fuvmpcwrbm',
      },
    ],
  },
  {
    key: 'khs',
    img: khsImg,
    donateUrl: 'https://khersonhumansafari.com/#card-yu9s7p992kfsik8',
    socialLinks: [
      {
        asset: webIcon,
        labelKey: 'help.website',
        href: 'https://khersonhumansafari.com/#card-yu9s7p992kfsik8',
      },
      {
        asset: facebookIcon,
        labelKey: 'help.facebook',
        href: 'https://www.facebook.com/profile.php?id=61577795517851',
      },
      {
        asset: instagramIcon,
        labelKey: 'help.instagram',
        href: 'https://www.instagram.com/khersonhumansafari/',
      },
      {
        asset: blueskyIcon,
        labelKey: 'help.bluesky',
        href: 'https://bsky.app/profile/khersonhumansafari.bsky.social',
      },
      { asset: xIcon, labelKey: 'help.x', href: 'https://x.com/KhersonHS2025' },
    ],
  },
] as const

// Kherson reporting. Titles are the publications' own, so they are not translated.
const reportingItems = [
  {
    publication: 'Kyiv Post',
    title: "Sadistic 'Human Safari': Russia's Drone Warfare in Kherson",
    byline: 'Zarina Zabrisky, 2024.10.08',
    href: 'https://www.kyivpost.com/analysis/40206',
  },
  {
    publication: 'Euromaidan Press',
    title: "Inside Human Safari: the film that captures Russia's drones hunting Ukrainians like prey",
    byline: 'Kostiantyn Doroshenko, 2025.08.22',
    href: 'https://euromaidanpress.com/2025/08/22/the-film-showing-how-russias-killer-drones-turned-ukraine-into-a-human-safari/',
  },
  {
    publication: 'Kyiv Independent',
    title: "Inside Russia's everyday manhunt of Ukrainians in Kherson",
    byline: 'Francis Farrell and Sasha Maslov, 2026.03.06',
    href: 'https://kyivindependent.com/postcards-from-kherson-the-city-where-russian-drones-hunt-civilians-every-day/',
  },
  {
    publication: 'Business Insider',
    title: "Russia is turning Kherson into a 'human safari' as its drones hunt civilians",
    byline: 'Ibrahim Naber, 2026.08.12',
    href: 'https://www.businessinsider.com/kherson-ukraine-human-safari-russia-drones-civilians-2026-8',
  },
  {
    publication: 'Tagesschau / ARD',
    title: 'Cherson: Leben mit der russischen Menschenjagd',
    byline: 'Rebecca Barth, 2026.08.24',
    href: 'https://www.tagesschau.de/ausland/europa/ukraine-cherson-130.html',
  },
  {
    publication: 'Kyiv Post',
    title: "Inside Kherson's Red Zone: A Kyiv Post Special Report",
    byline: 'Korbinian Leo Kramer, 2026.08.27',
    href: 'https://www.kyivpost.com/post/82823',
  },
  {
    publication: 'Kyiv Post',
    title: 'In Kherson, the Front Line Runs Through Everyday Life',
    byline: 'Korbinian Leo Kramer, 2026.08.28',
    href: 'https://www.kyivpost.com/post/83134',
  },
] as const

export default function Help() {
  const { t } = useTranslation()

  return (
    <div className='w-full text-gray-900 flex flex-col text-lg'>
      <section className='px-6 md:px-20 py-16 bg-gray-100 flex flex-col items-center gap-16'>
        <div className='text-center max-w-2xl flex flex-col gap-8'>
          <h1 className='text-4xl md:text-5xl font-bold'>{t('help.pageTitle')}</h1>
          <p className='text-lg opacity-70'>{t('help.sectionIntro')}</p>
          <p className='text-lg opacity-70'>{t('help.sectionIntro2')}</p>
          <p className='text-lg opacity-70'>
            {t('help.sectionIntro3')} <strong>{t('help.sectionIntroBold')}</strong>
          </p>
        </div>

        <MediaCarousel items={khersonMedia} className='max-w-3xl' />

        {/* Tell people */}
        <div className='flex flex-col gap-6 w-full max-w-5xl'>
          <h2 className='text-2xl font-bold'>{t('help.step3Title')}</h2>
          <p className='opacity-80'>
            {t('help.step3Para')} <strong>#StopHumanSafari #SaveKherson</strong>{' '}
            {t('help.step3ParaPost')}
          </p>
          <p className='font-semibold'>{t('help.shareTitle')}</p>
          <div className='flex gap-4 flex-col md:flex-row'>
            <a
              href={shareLinks.facebook}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-800 border-blue-700 border-2 px-4 py-2 rounded-md flex items-center gap-2'
            >
              {t('help.share', { platform: '' })}{' '}
              <img src={facebookIcon} alt={t('help.facebook')} className='h-5 w-5' />
            </a>
            <a
              href={shareLinks.x}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-800 border-blue-700 border-2 px-4 py-2 rounded-md flex items-center gap-2'
            >
              {t('help.share', { platform: '' })}{' '}
              <img src={xIcon} alt={t('help.x')} className='h-5 w-5' />
            </a>
            <a
              href={shareLinks.bluesky}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-800 border-blue-700 border-2 px-4 py-2 rounded-md flex items-center gap-2'
            >
              {t('help.share', { platform: '' })}{' '}
              <img src={blueskyIcon} alt={t('help.bluesky')} className='h-5 w-5' />
            </a>
          </div>
          <p className='opacity-60'>#StopHumanSafari #SaveKherson</p>
        </div>

        {/* Organise */}
        <div className='flex flex-col gap-4 w-full max-w-5xl'>
          <h2 className='text-2xl font-bold'>{t('help.step2Title')}</h2>
          <ul className='list-disc ml-6 flex flex-col gap-4'>
            <li>
              <span className='font-medium'>{t('help.step2Bullet1')}</span>
              <p className='mt-1 opacity-80'>
                {t('help.filmScreening')}{' '}
                <a
                  href='https://www.khersonhumansafari.com'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 underline'
                >
                  khersonhumansafari.com
                </a>
              </p>
              <p className='mt-1 opacity-80'>
                {t('help.organise')}{' '}
                <a className='text-blue-700 underline' href={`mailto:${t('help.email')}`}>
                  {t('help.email')}
                </a>
              </p>
            </li>
            <li>
              <span className='font-medium'>{t('help.step2Bullet2')}</span>
              <p className='mt-1 opacity-80'>
                {t('help.publicActionLead')}{' '}
                {t('help.reachOutPre')}{' '}
                <a className='text-blue-700 underline' href={`mailto:${t('help.actionEmail')}`}>
                  {t('help.reachOut')}
                </a>{' '}
                {t('help.reachOutPost')}
              </p>
            </li>
            <li>
              <span className='font-medium'>{t('help.step2Bullet3')}</span>
              <p className='mt-1 opacity-80'>
                {t('help.newsCoverageLead')}{' '}
                {t('help.putInTouchPre')}{' '}
                <a className='text-blue-700 underline' href={`mailto:${t('help.actionEmail')}`}>
                  {t('help.putInTouch')}
                </a>
              </p>
            </li>
          </ul>
        </div>

        <Donate orgs={orgs} introKey='help.donateIntro' />

        {/* Policy */}
        <div className='flex flex-col gap-6 w-full max-w-5xl'>
          <h2 className='text-2xl font-bold'>{t('help.policyTitle')}</h2>
          <p className='opacity-80'>{t('help.policyBody')}</p>
          <p className='opacity-80'>
            {t('help.policyHelp')}{' '}
            <a href={`mailto:${t('help.actionEmail')}`} className='text-blue-600 underline'>
              {t('help.getInTouch')} →
            </a>
          </p>
        </div>

        <Follow groups={khersonSources} subKey='follow.subHumanSafari' />

        {/* Inform */}
        <div className='flex flex-col gap-6 w-full max-w-5xl'>
          <div className='flex flex-col gap-3'>
            <h2 className='text-2xl font-bold'>{t('help.inform')}</h2>
            <p className='opacity-70'>{t('help.informSub')}</p>
          </div>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-1'>
              <strong>{t('resources.linkLibraryTitle')}</strong>
              <p className='text-base opacity-70'>{t('resources.linkLibraryDesc')}</p>
              <a
                href='https://linktr.ee/Kherson_Human_Safari'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline break-all'
              >
                https://linktr.ee/Kherson_Human_Safari
              </a>
            </div>
            <div className='flex flex-col gap-1'>
              <strong>{t('resources.humanSafariMapTitle')}</strong>
              <p className='text-base opacity-70'>{t('resources.humanSafariMapDesc')}</p>
              <a
                href='https://maps.tochnyi.info/humansafari/'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline break-all'
              >
                https://maps.tochnyi.info/humansafari
              </a>
            </div>
            <div className='flex flex-col gap-1'>
              <strong>{t('resources.truthHoundsTitle')}</strong>
              <a
                href='https://truth-hounds.org/en/cases/killzone-how-russian-drones-are-devastating-the-river-dnipros-right-bank/'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline'
              >
                {t('resources.truthHoundsDesc')}
              </a>
            </div>
            <div className='flex flex-col gap-1'>
              <strong>{t('resources.hrwTitle')}</strong>
              <a
                href='https://www.hrw.org/report/2025/06/03/hunted-from-above/russias-use-of-drones-to-attack-civilians-in-kherson-ukraine'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline'
              >
                {t('resources.hrwDesc')}
              </a>
            </div>
            <div className='flex flex-col gap-1'>
              <strong>{t('resources.unReportsTitle')}</strong>
              <a
                href='https://www.ohchr.org/sites/default/files/documents/hrbodies/hrcouncil/coiukraine/a-hrc-59-crp2-en.pdf'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline block'
              >
                {t('resources.report1')}
              </a>
              <a
                href='https://www.ohchr.org/en/hr-bodies/hrc/iicihr-ukraine/reports/report-independent-international-commission-inquiry-ukraine-un-general-assembly-a80497'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline block'
              >
                {t('resources.report3')}
              </a>
              <a
                href='https://www.ohchr.org/sites/default/files/documents/hrbodies/hrcouncil/coiukraine/a-80-497-advance-unedited-version-en.pdf'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline'
              >
                {t('resources.guidelines')}
              </a>
            </div>
            <div className='flex flex-col gap-1'>
              <strong>{t('resources.wikipediaTitle')}</strong>
              <a
                href='https://en.wikipedia.org/wiki/Human_safari_(terror_campaign)'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline'
              >
                {t('resources.english')}
              </a>
            </div>
            <div className='flex flex-col gap-1'>
              <strong>{t('help.disdict.title')}</strong>
              <a
                href='https://disdict.org/warcrimes/humansafari'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline'
              >
                {t('help.disdict.link')}
              </a>
            </div>
            <div className='flex flex-col gap-1'>
              <strong>{t('resources.documentaryTitle')}</strong>
              <a
                href='https://www.khersonhumansafari.com'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline'
              >
                www.khersonhumansafari.com
              </a>
            </div>
            {reportingItems.map(({ publication, title, byline, href }) => (
              <div key={href} className='flex flex-col gap-1'>
                <strong>{publication}</strong>
                <a
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 underline'
                >
                  {title}
                </a>
                <p className='text-sm opacity-60'>{byline}</p>
              </div>
            ))}
            <ResourceGroups groups={humanSafariNews} />
          </div>
        </div>

        <DownloadGraphics id='kherson-graphics' images={graphics} />
      </section>

      {/* How it started */}
      <section className='flex justify-center items-center py-12 bg-white'>
        <Link to={routes.action.path} className='text-blue-700 underline text-xl'>
          {t('help.howItStarted')} →
        </Link>
      </section>
    </div>
  )
}
